/**
 * The site's icon subset, from the 21-icon set of the approved direction
 * (24px grid, 2px stroke, round caps — Manrope's rounded-geometric register).
 * The energy treatments reuse the symbol's concentric arcs, so the icons share
 * a visual root with the logo instead of reading as a stock pack.
 *
 * Used where an icon carries information, not beside every heading — a
 * decorative icon on everything makes the meaningful ones stop registering.
 */
import type { SVGProps } from "react";

const base = (props: SVGProps<SVGSVGElement>) => ({
  viewBox: "0 0 24 24",
  width: 18,
  height: 18,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
  ...props,
});

export const IconWaves = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M7.5 5.5a11 11 0 0 1 0 13M11.8 7.6a7 7 0 0 1 0 8.8M16.1 9.6a3.2 3.2 0 0 1 0 4.8" /></svg>
);
export const IconPulse = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M2.5 12h4l2.2-6 3.4 12L15 12h6.5" /></svg>
);
export const IconHands = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M8.5 13V5.4a1.7 1.7 0 0 1 3.4 0V12" /><path d="M11.9 11.4V4.6a1.7 1.7 0 0 1 3.4 0V12" /><path d="M15.3 12V7.4a1.7 1.7 0 0 1 3.4 0V15a6.5 6.5 0 0 1-6.5 6.5h-.7A6.5 6.5 0 0 1 5 15v-2.6a1.7 1.7 0 0 1 3.4 0" /></svg>
);
export const IconNeedle = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="m4 20 8.5-8.5M14 10l6-6M12.5 11.5 16 15l4-4-3.5-3.5Z" /></svg>
);
export const IconFitness = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M4 9v6M7 6.5v11M17 6.5v11M20 9v6M7 12h10" /></svg>
);
export const IconHome = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="m3.5 10.5 8.5-7 8.5 7" /><path d="M5.5 9.4V20h13V9.4" /><path d="M10 20v-5.5h4V20" /></svg>
);
export const IconCup = (p: SVGProps<SVGSVGElement>) => (
  // suction cup on skin: valve stub, OPEN-bottomed dome, and the skin line
  // drawn up into the cup mouth — the negative-pressure gesture. The open
  // bottom is what keeps it from reading as a notification bell.
  <svg {...base(p)}><path d="M12 4.5V7" /><path d="M7 18v-6a5 5 0 0 1 10 0v6" /><path d="M2.5 18h4.5c2.5 0 2.5-2 5-2s2.5 2 5 2h4.5" /></svg>
);
export const IconShield = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M12 2.7 4.5 6v6.2c0 4.5 3.2 7.9 7.5 9.1 4.3-1.2 7.5-4.6 7.5-9.1V6Z" /><path d="m8.8 12.2 2.3 2.3 4.1-4.6" /></svg>
);
export const IconStar = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base({ fill: "currentColor", stroke: "none", ...p })}><path d="M12 2.8l2.85 5.77 6.37.93-4.61 4.49 1.09 6.34L12 17.34l-5.7 3 1.09-6.35-4.61-4.49 6.37-.93z" /></svg>
);
export const IconCheck = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="m4.5 12.5 5 5 10-11" /></svg>
);
export const IconClock = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><circle cx="12" cy="12" r="9" /><path d="M12 7v5.2l3.2 2" /></svg>
);
export const IconPin = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M20 10.5c0 5.2-8 11-8 11s-8-5.8-8-11a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10.2" r="2.8" /></svg>
);
export const IconMail = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><rect x="3" y="5" width="18" height="14" rx="3" /><path d="m4 7.5 7.1 5a1.6 1.6 0 0 0 1.8 0l7.1-5" /></svg>
);
export const IconSpine = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M12 3v18" /><path d="M9 5.5h6M8.5 9.2h7M8.5 12.9h7M9 16.6h6" /></svg>
);
export const IconPeople = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><circle cx="9" cy="8" r="3.4" /><path d="M2.8 20a6.4 6.4 0 0 1 12.4 0" /><path d="M16.2 5a3.4 3.4 0 0 1 0 6.6M17.4 14.4A6.4 6.4 0 0 1 21.2 20" /></svg>
);
export const IconDoc = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M14 3H7.5A2.5 2.5 0 0 0 5 5.5v13A2.5 2.5 0 0 0 7.5 21h9a2.5 2.5 0 0 0 2.5-2.5V8Z" /><path d="M14 3v5h5M8.5 13h7M8.5 16.5h4.5" /></svg>
);
export const IconQuestion = (p: SVGProps<SVGSVGElement>) => (
  // FAQ: a speech bubble carrying a question mark — questions as a
  // conversation, not a plain help-circle
  <svg {...base(p)}><path d="M7.9 20A9 9 0 1 0 4 16.1L3 21Z" /><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" /><path d="M12 17h.01" /></svg>
);
export const IconCalendar = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><rect x="3" y="5" width="18" height="16" rx="3" /><path d="M8 3v4M16 3v4M3 11h18" /></svg>
);
export const IconPhone = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L8.1 9.5a16 16 0 0 0 6 6l1.1-1.1a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2Z" /></svg>
);

/**
 * Treatment icon by slug. The arc family goes to the therapies that transmit
 * energy; a slug map rather than a CMS field — an editor picking icons per
 * entry is a decision the owner has not asked for, and this stays correct for
 * the ten migrated slugs without any admin work. Swap for a model enum later
 * if editors want control.
 */
export const treatmentIconFor = (slug?: string) => {
  switch (slug) {
    case "tecar-therapy":
    case "therapeia-reymata-tens-yperixos": return IconWaves;
    case "shockwave-kroustikos-yperixos": return IconPulse;
    case "xeirotherapeia":
    case "therapeytiki-malaksi": return IconHands;
    case "therapeia-ksiras-velonas-dry-needling":
    case "ergon-therapy": return IconNeedle;
    case "therapeytiki-askisi-endynamosi": return IconFitness;
    case "therapeia-me-ventouzes": return IconCup;
    case "therapeia-kat-oikon": return IconHome;
    case "synedries-eopyy": return IconShield;
    default: return null;
  }
};
